import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RaceCard as RaceCardType } from '../../types';
import { pickHero } from './heroImages';
import './RaceCard.css';

interface Props {
  race: RaceCardType;
}

const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
  open:     { label: 'Open',        cls: 'status-open' },
  closed:   { label: 'Closed',      cls: 'status-closed' },
  sold_out: { label: 'Sold Out',    cls: 'status-sold-out' },
  upcoming: { label: 'Coming Soon', cls: 'status-upcoming' },
  unknown:  { label: 'See Site',    cls: 'status-unknown' },
};

const TERRAIN_LABEL: Record<string, string> = {
  trail: '⛰ Trail',
  road:  '🏙 Road',
  track: '🏟 Track',
  mixed: '🗺 Mixed',
};

const TIER_BADGE: Record<string, { label: string; cls: string }> = {
  world_major:  { label: 'World Major',  cls: 'tier-world-major' },
  gold_label:   { label: 'Gold Label',   cls: 'tier-gold-label' },
  major:        { label: 'Major Race',   cls: 'tier-major' },
  iconic_ultra: { label: 'Iconic Ultra', cls: 'tier-iconic-ultra' },
};


function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`;
}

function StarRating({ rating }: { rating: number }) {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return (
    <span className="race-card-stars">
      {'★'.repeat(full)}{half ? '½' : ''}{'☆'.repeat(5 - full - (half ? 1 : 0))}
      <span className="race-card-rating-num">{rating.toFixed(1)}</span>
    </span>
  );
}

const RaceCard: React.FC<Props> = ({ race }) => {
  const navigate = useNavigate();
  const status = STATUS_LABELS[race.registration_status] || STATUS_LABELS.unknown;
  const tier = race.race_tier ? TIER_BADGE[race.race_tier] : null;

  return (
    <div className="race-card" onClick={() => navigate(`/races/${race.slug}`)}>
      <div
        className="race-card-image"
        style={{ backgroundImage: `url(${race.hero_image_url || pickHero(race.id, race.terrain)})` }}
      />

      <div className="race-card-body">
        <div className="race-card-header">
          <span className={`race-card-status ${status.cls}`}>{status.label}</span>
          {race.terrain && (
            <span className={`race-card-terrain terrain-${race.terrain}`}>{TERRAIN_LABEL[race.terrain] || race.terrain}</span>
          )}
        </div>
        {tier && (
          <span className={`race-card-tier-badge ${tier.cls}`}>{tier.label}</span>
        )}

        <h3 className="race-card-name">{race.name}</h3>

        <div className="race-card-meta">
          <span className="race-card-date">{formatDate(race.race_date)}</span>
          {(race.city || race.state) && (
            <span className="race-card-location">
              {[race.city, race.state].filter(Boolean).join(', ')}
            </span>
          )}
        </div>

        {race.distances.length > 0 && (
          <div className="race-card-distances">
            {race.distances.slice(0, 4).map(d => (
              <span key={d} className="race-card-dist-tag">{d}</span>
            ))}
            {race.distances.length > 4 && (
              <span className="race-card-dist-tag race-card-dist-more">+{race.distances.length - 4}</span>
            )}
          </div>
        )}

        <div className="race-card-footer">
          {race.composite_rating ? (
            <StarRating rating={race.composite_rating} />
          ) : (
            <span className="race-card-no-reviews">No reviews yet</span>
          )}
          {race.price_cents_min != null && (
            <span className="race-card-price">from {formatPrice(race.price_cents_min)}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default RaceCard;
